import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSearchParams } from "react-router-dom";
import { useLearning } from "src/hook/data/useLearning";
import { useWords } from "src/hook/data/useWords";
import { SortType, SpecialWordListId } from "src/model/enum/enums";
import { Word } from "src/model/Word";
import styles from "./ListView.module.scss";
import ListViewContent from "./ListViewContent/ListViewContent";
import { ListViewHeader } from "./ListViewHeader/ListViewHeader";


const ListView = () => {
    const { t } = useTranslation();
    const { learningInfo } = useLearning();
    const [searchParams] = useSearchParams();
    const wordListId = searchParams.get('id');
    const [currentWords, setCurrentWords] = useState<Word[]>([]);
    const { getWordsByWordList, words } = useWords();
    const [selectedSort, setSelectedSort] = useState<SortType>(SortType.EasiestFirst);


    const sortedWords = useMemo(() => {
        return [...currentWords].sort((a, b) => {
            switch (selectedSort) {
                case SortType.Alphabetic:
                    return a.Text.localeCompare(b.Text);
                case SortType.HardestFirst:
                    return (b.Rank ?? 0) - (a.Rank ?? 0);
                case SortType.EasiestFirst:
                    return (a.Rank ?? 0) - (b.Rank ?? 0);
                default:
                    return 0;
            }
        });
    }, [currentWords, selectedSort]);

    useEffect(() => {
        if (!wordListId || !words?.length) return;
        if (wordListId === SpecialWordListId.Known) {
            setCurrentWords(getWordsByWordList(
                learningInfo?.KnownWordIds ?? []
            ));
        }
        else if (wordListId === SpecialWordListId.ToLearn) {
            setCurrentWords(getWordsByWordList(
                learningInfo?.ToLearn ?? []
            ));
        }
        else if (wordListId === SpecialWordListId.Unknown) {
            setCurrentWords(getWordsByWordList(
                learningInfo?.UnknownWordIds.filter
                    (id => !learningInfo?.ToLearn.includes(id)) ?? []
            ));
        }
    }, [wordListId, learningInfo, getWordsByWordList, words]);

    return (
        <section className={styles.listView}>
            <ListViewHeader
                selectedItem={selectedSort}
                setSelectedItem={setSelectedSort}
                title={wordListId === SpecialWordListId.Known ? t('Known') : wordListId === SpecialWordListId.ToLearn ? t('Not started') : t('Learning')}
            />
            <ListViewContent
                sortedWords={sortedWords}
            />
        </section>
    );
};

export default memo(ListView);